/**
 * CommentMarginZone — Right margin container for inline document comments (v2).
 *
 * Phase 8: Section-aligned positioning. Comment groups are absolutely
 * positioned to align with their corresponding section in the document.
 * The margin scrolls with the document (not independently). When a
 * section's comments overflow its height, the group gets internal scroll.
 *
 * See: docs/features/document-system/document-comments.md
 */
import React, { useState, useCallback, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { X, ChevronDown, ChevronUp, Plus } from "lucide-react";
import Tooltip from "@mui/material/Tooltip";
import CommentCard from "./CommentCard";
import useCommentPositions from "../../hooks/useCommentPositions";
import "../../css/comment-margin.css";

const COLLAPSED_LIMIT = 2;

// ---------- Inline comment input ----------

const CommentInlineInput = ({ sectionKey, sectionTitle, anchor, onSubmit, onCancel }) => {
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const savingRef = useRef(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleSubmit = useCallback(async () => {
    const trimmed = body.trim();
    if (!trimmed || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setError(false);
    try {
      const id = await onSubmit(trimmed, sectionKey, anchor || null);
      if (!id) { setError(true); return; }
      setBody("");
      onCancel();
    } catch {
      setError(true);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }, [body, sectionKey, anchor, onSubmit, onCancel]);

  return (
    <div className="comment-inline-input" data-section-key={sectionKey} aria-busy={saving}>
      <div className="comment-inline-input-label">
        {sectionTitle}
        {anchor?.textSnippet && (
          <span className="comment-inline-input-snippet">
            &ldquo;{anchor.textSnippet}&rdquo;
          </span>
        )}
        {anchor?.type === "row" && anchor.rowLabel && (
          <span className="comment-inline-input-snippet">
            Row: {anchor.rowLabel}
          </span>
        )}
      </div>
      <textarea
        ref={textareaRef}
        className="comment-inline-input-textarea"
        placeholder="Add a comment…"
        value={body}
        readOnly={saving}
        onChange={(e) => {
          setBody(e.target.value);
          e.target.style.height = "auto";
          e.target.style.height = e.target.scrollHeight + "px";
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(); }
          if (e.key === "Escape" && !savingRef.current) onCancel();
        }}
      />
      {error && <div className="comment-write-error" role="alert">Couldn't post comment. Try again.</div>}
      <div className="comment-inline-input-actions">
        <button onClick={onCancel} disabled={saving}>Cancel</button>
        <button
          className="comment-inline-input-submit"
          onClick={handleSubmit}
          disabled={saving || !body.trim()}
        >
          {saving ? "Posting…" : error ? "Retry" : "Comment"}
        </button>
      </div>
    </div>
  );
};

// ---------- main component ----------

const CommentMarginZone = ({
  isOpen,
  readOnly = false,
  onClose,
  comments,
  sectionOrder = [],
  onAddComment,
  onEditComment,
  onDeleteComment,
  currentUserId,
  currentUserRole,
  activeSectionKey,
  activeSectionTitle,
  pendingAnchor,
  onClearActiveSection,
  onStartNewComment,
  contentContainerRef,
}) => {
  // Measure section positions for aligned comment placement
  const positions = useCommentPositions(contentContainerRef, sectionOrder, [comments.length]);

  // Track which sections are expanded (show all comments vs collapsed)
  const [expandedSections, setExpandedSections] = useState(new Set());
  const toggleExpanded = useCallback((key) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  // Refs for measuring rendered section group heights (for push-down stacking)
  const sectionRefs = useRef({});
  const [stackedPositions, setStackedPositions] = useState(new Map());

  // Group comments by sectionKey (null → "general")
  const sectionMap = useMemo(() => {
    const map = new Map();
    comments.forEach((c) => {
      const key = c.sectionKey || "general";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    });
    return map;
  }, [comments]);

  // Normalize null activeSectionKey to "general" for document-level comments
  const normalizedActiveKey = activeSectionKey === null && activeSectionTitle
    ? "general"
    : activeSectionKey;

  // Build ordered list: ALL sections from sectionOrder (for empty-space preservation)
  // plus "general" if it has comments or is the active target
  const sectionKeys = useMemo(() => {
    const keys = [];
    if (sectionMap.has("general") || normalizedActiveKey === "general") keys.push("general");
    sectionOrder.forEach((s) => {
      keys.push(s.sectionKey);
    });
    if (normalizedActiveKey && !keys.includes(normalizedActiveKey)) {
      keys.push(normalizedActiveKey);
    }
    return keys;
  }, [sectionMap, normalizedActiveKey, sectionOrder]);

  const getSectionTitle = useCallback((key) => {
    if (key === "general") return "General";
    const match = sectionOrder.find((s) => s.sectionKey === key);
    return match?.title || key;
  }, [sectionOrder]);

  // Compute stacked positions AFTER render so refs have real heights.
  useLayoutEffect(() => {
    if (!isOpen) return;
    // Absolute groups ignore the body's padding. Reserve the toolbar's space
    // so the first comment's actions remain reachable below Add and Close.
    let prevBottom = 30;
    const stacked = new Map();
    sectionKeys.forEach((key) => {
      const sectionComments = sectionMap.get(key) || [];
      const isActiveSection = key === normalizedActiveKey;
      if (sectionComments.length === 0 && !isActiveSection) return;

      const pos = positions.get(key);
      const desiredTop = pos ? pos.top : prevBottom;
      const top = Math.max(desiredTop, prevBottom);
      stacked.set(key, top);

      const el = sectionRefs.current[key];
      const height = el ? el.offsetHeight : 0;
      prevBottom = top + height + 8; // 8px gap between groups
    });
    setStackedPositions(stacked);
  }, [isOpen, sectionKeys, sectionMap, normalizedActiveKey, positions, expandedSections, comments]);

  if (!isOpen) return null;

  const totalCount = comments.length;
  const hasContent = sectionKeys.length > 0 || normalizedActiveKey;

  return (
    <div
      className="comment-margin-zone userback-ignore"
      role="complementary"
      aria-label="Comments"
    >
      {/* Close button — top-right, scrolls with content */}
      <Tooltip title="Close comments" arrow>
        <button
          className="comment-margin-close-btn"
          onClick={onClose}
          aria-label="Close comments"
        >
          <X size={14} />
        </button>
      </Tooltip>

      {/* Add-comment button — activates a general-section input so users can
          compose a comment without first clicking a section-level affordance. */}
      {!readOnly && onStartNewComment && (
        <Tooltip title="Add comment" arrow>
          <button
            className="comment-margin-add-btn"
            onClick={onStartNewComment}
            aria-label="Add comment"
          >
            <Plus size={14} />
          </button>
        </Tooltip>
      )}

      {/* Body — relatively positioned; section groups absolutely positioned within */}
      <div className="comment-margin-body">
        {sectionKeys.map((key) => {
          const sectionComments = sectionMap.get(key) || [];
          const isActiveSection = key === normalizedActiveKey;
          const hasContent = sectionComments.length > 0 || isActiveSection;

          const stackedTop = stackedPositions.get(key);
          const style = stackedTop !== undefined
            ? { position: "absolute", top: stackedTop, left: 20, right: 16 }
            : { marginLeft: 20, marginRight: 16 };

          const isExpanded = expandedSections.has(key);
          const hiddenCount = sectionComments.length - COLLAPSED_LIMIT;
          const showCollapse = sectionComments.length > COLLAPSED_LIMIT;
          const visibleComments = showCollapse && !isExpanded
            ? sectionComments.slice(0, COLLAPSED_LIMIT)
            : sectionComments;

          return (
            <div
              key={key}
              ref={(el) => { sectionRefs.current[key] = el; }}
              className={`comment-margin-section${hasContent ? " comment-margin-section--has-content" : ""}`}
              data-section-key={key}
              style={style}
            >
              {hasContent && (
                <>
                  {visibleComments.map((comment) => (
                    <CommentCard
                      key={comment.id}
                      comment={comment}
                      currentUserId={currentUserId}
                      currentUserRole={currentUserRole}
                      readOnly={readOnly}
                      onEdit={onEditComment}
                      onDelete={onDeleteComment}
                    />
                  ))}
                  {showCollapse && (
                    <button
                      className="comment-margin-toggle-btn"
                      onClick={() => toggleExpanded(key)}
                    >
                      {isExpanded ? (
                        <>Show less <ChevronUp size={10} /></>
                      ) : (
                        <>+{hiddenCount} more <ChevronDown size={10} /></>
                      )}
                    </button>
                  )}
                  {!readOnly && isActiveSection && onAddComment && (
                    <CommentInlineInput
                      sectionKey={activeSectionKey}
                      sectionTitle={activeSectionTitle || getSectionTitle(key)}
                      anchor={pendingAnchor}
                      onSubmit={onAddComment}
                      onCancel={onClearActiveSection}
                    />
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default React.memo(CommentMarginZone);
